import type { User } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type Timestamp,
  type Unsubscribe
} from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import type {
  SupportKnowledgeEntry,
  SupportMessageAuthorRole,
  SupportTicket,
  SupportTicketAttachment,
  SupportTicketContext,
  SupportTicketMessage,
  SupportTicketStatus,
  SupportTicketType
} from "../types/imobi";
import { db, storage } from "./firebase";

export type SaveSupportKnowledgeEntryInput = {
  question: string;
  keywords: string[];
  answer: string;
};

export type CreateSupportTicketInput = {
  companyId?: string;
  companyName?: string;
  type: SupportTicketType;
  subject: string;
  description: string;
  suggestedArticleIds: string[];
  /** Screenshot of the screen the user was on, uploaded before the ticket is created. */
  screenshot?: Blob;
  context?: SupportTicketContext;
};

function mapAttachments(value: unknown): SupportTicketAttachment[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === "object" && typeof (item as Record<string, unknown>).url === "string")
    .map((item) => ({
      url: String(item.url),
      path: String(item.path ?? ""),
      name: String(item.name ?? "anexo"),
      contentType: String(item.contentType ?? "image/jpeg"),
      kind: "screenshot" as const
    }));
}

function mapContext(value: unknown): SupportTicketContext | undefined {
  if (!value || typeof value !== "object") {
    return undefined;
  }
  const data = value as Record<string, unknown>;
  const text = (key: string) => (typeof data[key] === "string" && data[key] ? String(data[key]) : undefined);
  return {
    viewId: text("viewId"),
    viewLabel: text("viewLabel"),
    url: text("url"),
    userAgent: text("userAgent"),
    viewport: text("viewport"),
    appVersion: text("appVersion"),
    theme: text("theme")
  };
}

function serializeTimestamp(value: unknown): string {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as Timestamp).toDate().toISOString();
  }

  return typeof value === "string" ? value : "";
}

function userName(user: User) {
  return user.displayName ?? user.email ?? user.uid;
}

function mapSupportTicket(id: string, data: Record<string, unknown>): SupportTicket {
  return {
    id,
    companyId: data.companyId ? String(data.companyId) : undefined,
    companyName: data.companyName ? String(data.companyName) : undefined,
    reporterUserId: String(data.reporterUserId ?? ""),
    reporterName: String(data.reporterName ?? ""),
    reporterEmail: data.reporterEmail ? String(data.reporterEmail) : undefined,
    type: (data.type ?? "duvida") as SupportTicketType,
    subject: String(data.subject ?? ""),
    description: String(data.description ?? ""),
    status: (data.status ?? "aberto") as SupportTicketStatus,
    suggestedArticleIds: Array.isArray(data.suggestedArticleIds) ? data.suggestedArticleIds.map(String) : [],
    assignedToUserId: data.assignedToUserId ? String(data.assignedToUserId) : undefined,
    assignedToUserName: data.assignedToUserName ? String(data.assignedToUserName) : undefined,
    resolutionNotes: data.resolutionNotes ? String(data.resolutionNotes) : undefined,
    resolvedAt: serializeTimestamp(data.resolvedAt) || undefined,
    lastMessageAt: serializeTimestamp(data.lastMessageAt) || undefined,
    lastMessageAuthorRole: data.lastMessageAuthorRole === "agent" || data.lastMessageAuthorRole === "reporter" ? data.lastMessageAuthorRole : undefined,
    lastMessagePreview: data.lastMessagePreview ? String(data.lastMessagePreview) : undefined,
    reporterLastReadAt: serializeTimestamp(data.reporterLastReadAt) || undefined,
    agentLastReadAt: serializeTimestamp(data.agentLastReadAt) || undefined,
    rating: typeof data.rating === "number" ? data.rating : undefined,
    ratingComment: data.ratingComment ? String(data.ratingComment) : undefined,
    ratedAt: serializeTimestamp(data.ratedAt) || undefined,
    attachments: mapAttachments(data.attachments),
    context: mapContext(data.context),
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? ""),
    updatedAt: serializeTimestamp(data.updatedAt),
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined
  };
}

export function subscribeMySupportTickets(uid: string | undefined, onChange: (tickets: SupportTicket[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !uid) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "supportTickets"), where("reporterUserId", "==", uid), orderBy("createdAt", "desc")),
    (snapshot) => onChange(snapshot.docs.map((ticketDoc) => mapSupportTicket(ticketDoc.id, ticketDoc.data()))),
    (error) => onError(error.message)
  );
}

export function subscribeAllSupportTickets(onChange: (tickets: SupportTicket[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "supportTickets"), orderBy("createdAt", "desc")),
    (snapshot) => onChange(snapshot.docs.map((ticketDoc) => mapSupportTicket(ticketDoc.id, ticketDoc.data()))),
    (error) => onError(error.message)
  );
}

export async function createSupportTicket(input: CreateSupportTicketInput, user: User): Promise<string> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  if (!input.description.trim()) {
    throw new Error("Descreva o problema ou a dúvida.");
  }

  const ticketRef = doc(collection(db, "supportTickets"));
  const attachments: SupportTicketAttachment[] = [];
  if (input.screenshot) {
    if (!storage) {
      throw new Error("Storage não configurado.");
    }
    const name = `tela-${new Date().toISOString().replace(/[:.]/g, "-")}.jpg`;
    const path = `supportTickets/${ticketRef.id}/${user.uid}/${name}`;
    const fileRef = ref(storage, path);
    await uploadBytes(fileRef, input.screenshot, { contentType: "image/jpeg" });
    attachments.push({ url: await getDownloadURL(fileRef), path, name, contentType: "image/jpeg", kind: "screenshot" });
  }

  await setDoc(ticketRef, {
    companyId: input.companyId ?? null,
    companyName: input.companyName ?? null,
    reporterUserId: user.uid,
    reporterName: userName(user),
    reporterEmail: user.email ?? null,
    type: input.type,
    subject: input.subject.trim() || "Chamado de suporte",
    description: input.description.trim(),
    status: "aberto",
    suggestedArticleIds: input.suggestedArticleIds,
    attachments,
    context: input.context ?? null,
    createdAt: serverTimestamp(),
    createdBy: user.uid
  });
  return ticketRef.id;
}

export async function updateSupportTicketStatus(
  ticketId: string,
  status: SupportTicketStatus,
  user: User,
  resolutionNotes?: string
): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  const now = serverTimestamp();
  await updateDoc(doc(db, "supportTickets", ticketId), {
    status,
    resolutionNotes: resolutionNotes?.trim() || null,
    resolvedAt: status === "resolvido" ? now : null,
    updatedAt: now,
    updatedBy: user.uid
  });
}

export async function claimSupportTicket(ticketId: string, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  await updateDoc(doc(db, "supportTickets", ticketId), {
    status: "em_atendimento",
    assignedToUserId: user.uid,
    assignedToUserName: userName(user),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

/** Permanently removes a ticket and its whole conversation (MASTER only, enforced by rules). */
export async function deleteSupportTicket(ticketId: string, attachments: SupportTicketAttachment[] = []): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  if (storage) {
    // A file already gone must not block erasing the ticket.
    await Promise.all(attachments.filter((attachment) => attachment.path).map((attachment) => deleteObject(ref(storage!, attachment.path)).catch(() => undefined)));
  }

  const messages = await getDocs(collection(db, "supportTickets", ticketId, "messages"));
  // Firestore batches take at most 500 writes.
  for (let start = 0; start < messages.docs.length; start += 450) {
    const batch = writeBatch(db);
    messages.docs.slice(start, start + 450).forEach((messageDoc) => batch.delete(messageDoc.ref));
    await batch.commit();
  }
  await deleteDoc(doc(db, "supportTickets", ticketId));
}

export function isSupportTicketOpen(ticket: SupportTicket): boolean {
  return ticket.status === "aberto" || ticket.status === "em_atendimento";
}

/** True when the other side sent a message the given side hasn't seen yet. */
export function isSupportTicketUnread(ticket: SupportTicket, viewerRole: SupportMessageAuthorRole): boolean {
  if (!ticket.lastMessageAt || !ticket.lastMessageAuthorRole || ticket.lastMessageAuthorRole === viewerRole) {
    return false;
  }
  const lastReadAt = viewerRole === "reporter" ? ticket.reporterLastReadAt : ticket.agentLastReadAt;
  return !lastReadAt || ticket.lastMessageAt > lastReadAt;
}

function mapSupportTicketMessage(id: string, data: Record<string, unknown>): SupportTicketMessage {
  return {
    id,
    authorUserId: String(data.authorUserId ?? ""),
    authorName: String(data.authorName ?? ""),
    authorRole: data.authorRole === "agent" ? "agent" : "reporter",
    text: String(data.text ?? ""),
    createdAt: serializeTimestamp(data.createdAt)
  };
}

export function subscribeSupportTicketMessages(ticketId: string, onChange: (messages: SupportTicketMessage[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "supportTickets", ticketId, "messages"), orderBy("createdAt")),
    // "estimate" keeps a message just sent in place while its server timestamp is pending.
    (snapshot) => onChange(snapshot.docs.map((messageDoc) => mapSupportTicketMessage(messageDoc.id, messageDoc.data({ serverTimestamps: "estimate" })))),
    (error) => onError(error.message)
  );
}

export async function sendSupportTicketMessage(ticket: SupportTicket, text: string, authorRole: SupportMessageAuthorRole, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  const trimmed = text.trim();
  if (!trimmed) {
    throw new Error("Escreva uma mensagem.");
  }
  if (!isSupportTicketOpen(ticket)) {
    throw new Error("Este chamado já foi encerrado.");
  }

  const now = serverTimestamp();
  const batch = writeBatch(db);
  batch.set(doc(collection(db, "supportTickets", ticket.id, "messages")), {
    authorUserId: user.uid,
    authorName: userName(user),
    authorRole,
    text: trimmed.slice(0, 4000),
    createdAt: now
  });

  const ticketUpdate: Record<string, ReturnType<typeof serverTimestamp> | string> = {
    lastMessageAt: now,
    lastMessageAuthorRole: authorRole,
    lastMessagePreview: trimmed.slice(0, 140),
    [authorRole === "agent" ? "agentLastReadAt" : "reporterLastReadAt"]: now,
    updatedAt: now,
    updatedBy: user.uid
  };
  // The first reply from the support team takes the ticket.
  if (authorRole === "agent" && ticket.status === "aberto") {
    ticketUpdate.status = "em_atendimento";
    ticketUpdate.assignedToUserId = user.uid;
    ticketUpdate.assignedToUserName = userName(user);
  }
  batch.update(doc(db, "supportTickets", ticket.id), ticketUpdate);
  await batch.commit();
}

export async function markSupportTicketRead(ticketId: string, viewerRole: SupportMessageAuthorRole, user: User): Promise<void> {
  if (!db) {
    return;
  }

  await updateDoc(doc(db, "supportTickets", ticketId), {
    [viewerRole === "agent" ? "agentLastReadAt" : "reporterLastReadAt"]: serverTimestamp(),
    updatedBy: user.uid
  });
}

export async function rateSupportTicket(ticketId: string, rating: number, comment: string, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
    throw new Error("Escolha de 1 a 5 estrelas.");
  }

  await updateDoc(doc(db, "supportTickets", ticketId), {
    rating,
    ratingComment: comment.trim().slice(0, 1000) || null,
    ratedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

function mapSupportKnowledgeEntry(id: string, data: Record<string, unknown>): SupportKnowledgeEntry {
  return {
    id,
    question: String(data.question ?? ""),
    keywords: Array.isArray(data.keywords) ? data.keywords.map(String) : [],
    answer: String(data.answer ?? ""),
    active: data.active !== false,
    createdAt: typeof data.createdAt === "string" ? data.createdAt : "",
    createdBy: typeof data.createdBy === "string" ? data.createdBy : "",
    updatedAt: typeof data.updatedAt === "string" ? data.updatedAt : undefined,
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : undefined
  };
}

export function subscribeSupportKnowledgeEntries(onChange: (entries: SupportKnowledgeEntry[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "supportKnowledgeEntries"), orderBy("createdAt")),
    (snapshot) => onChange(snapshot.docs.map((entryDoc) => mapSupportKnowledgeEntry(entryDoc.id, entryDoc.data()))),
    (error) => onError(error.message)
  );
}

export async function createSupportKnowledgeEntry(input: SaveSupportKnowledgeEntryInput, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  if (!input.question.trim() || !input.answer.trim()) {
    throw new Error("Informe a pergunta e a resposta.");
  }

  await setDoc(doc(collection(db, "supportKnowledgeEntries")), {
    question: input.question.trim(),
    keywords: input.keywords,
    answer: input.answer.trim(),
    active: true,
    createdAt: serverTimestamp(),
    createdBy: user.uid
  });
}

export async function updateSupportKnowledgeEntry(entryId: string, input: SaveSupportKnowledgeEntryInput, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  if (!input.question.trim() || !input.answer.trim()) {
    throw new Error("Informe a pergunta e a resposta.");
  }

  await updateDoc(doc(db, "supportKnowledgeEntries", entryId), {
    question: input.question.trim(),
    keywords: input.keywords,
    answer: input.answer.trim(),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

export async function setSupportKnowledgeEntryActive(entryId: string, active: boolean, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  await updateDoc(doc(db, "supportKnowledgeEntries", entryId), {
    active,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}
